import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where, doc, getDoc, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { useProgress } from '../contexts/ProgressContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getLessonUrl, courseNameToSlug } from '../utils/urlUtils';
import Header from './Header';
import { CourseType, CourseTitle, TitleLarge, TitleMedium, BodyMedium, LabelMedium, LabelSmall } from './Typography';

/**
 * Continue Learning Hero Card
 * Prominent card showing the user's current course/lesson to resume
 */
const ContinueLearningCard = ({ course, lesson, level, allLessons, progressPercentage, onClick, loading }) => {
  const { themeColors } = useTheme();
  
  if (loading) {
    return (
      /* role=status announces loading to screen readers (WCAG 4.1.3 Status Messages) */
      <div role="status" aria-live="polite" aria-label="Loading continue learning card" className="rounded-xl p-8 border" style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}>
        <div aria-hidden="true" className="animate-pulse">
          <div className="h-6 rounded w-1/3 mb-4" style={{ backgroundColor: themeColors.border }}></div>
          <div className="h-8 rounded w-2/3 mb-6" style={{ backgroundColor: themeColors.border }}></div>
          <div className="h-2 rounded w-full mb-4" style={{ backgroundColor: themeColors.border }}></div>
          <div className="h-4 rounded w-1/2" style={{ backgroundColor: themeColors.border }}></div>
        </div>
      </div>
    );
  }

  if (!course || !lesson) {
    return (
      <button
        type="button"
        className="w-full text-left bg-gradient-to-br from-[#0A4872] to-[#0d5a8a] rounded-xl p-8 border border-[#0d5a8a] cursor-pointer transform hover:scale-[1.02] transition-all duration-200 shadow-lg focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0A4872]"
        onClick={onClick}
        aria-label="Browse courses — begin your writing journey"
      >
        <LabelSmall className="text-blue-200 mb-2 uppercase tracking-wide">Get Started</LabelSmall>
        <TitleMedium className="!text-white mb-2">Ready to start learning?</TitleMedium>
        <BodyMedium className="!text-white mb-6 opacity-90">Begin your writing journey by exploring our courses.</BodyMedium>
        <div className="flex items-center text-white">
          <span className="font-semibold">Browse Courses</span>
          <span className="ml-2" aria-hidden="true">→</span>
        </div>
      </button>
    );
  }

  const levelLessons = allLessons.filter(l => l.level === level);
  const totalLessons = levelLessons.length;
  const currentLessonNumber = lesson?.lesson_number || 1;

  return (
    <button
      type="button"
      className="w-full text-left bg-gradient-to-br from-[#0A4872] to-[#0d5a8a] rounded-xl p-8 cursor-pointer transform hover:scale-[1.02] transition-all duration-200 shadow-lg border border-[#0d5a8a] focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-[#0A4872]"
      onClick={onClick}
      aria-label={`Continue learning ${course.title}, Level ${level}, Lesson ${currentLessonNumber} of ${totalLessons}`}
    >
      <LabelSmall className="text-blue-200 mb-2 uppercase tracking-wide">Continue Learning</LabelSmall>
      <CourseType className="!text-white text-sm mb-2 opacity-90">
        {course.type || 'HOW TO WRITE A'}
      </CourseType>
      <CourseTitle className="!text-white text-2xl mb-4 leading-tight">
        {course.title}
      </CourseTitle>
      
      <div className="mb-6">
        {/* Progress bar is decorative; progress value is in the aria-label above */}
        <div className="h-2 bg-blue-900/50 rounded-full overflow-hidden" aria-hidden="true">
          <div 
            className="h-full bg-white transition-all duration-300 rounded-full" 
            style={{ width: `${progressPercentage || 0}%` }} 
          />
        </div>
      </div>

      <div className="flex items-center justify-between text-white">
        <div>
          <LabelMedium className="!text-white">Level {level} • Lesson {currentLessonNumber}/{totalLessons}</LabelMedium>
        </div>
        <div className="text-white font-semibold" aria-hidden="true">→</div>
      </div>
    </button>
  );
};

/**
 * Active Course Card
 * Shows a course that's in progress with progress bar
 */
const ActiveCourseCard = ({ course, progressData, onClick }) => {
  const { themeColors } = useTheme();
  if (!progressData || !progressData.hasActive) return null;

  return (
    <button
      type="button"
      className="w-full text-left rounded-lg p-6 cursor-pointer transform hover:scale-[1.02] transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{ 
        backgroundColor: themeColors.card, 
        borderColor: themeColors.border 
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = themeColors.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = themeColors.border;
      }}
      onClick={onClick}
      aria-label={`${course.title}: ${progressData.completed} of ${progressData.total} lessons completed`}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <CourseType className="text-xs mb-1" style={{ color: themeColors.textSecondary }}>
            {course.type || 'HOW TO WRITE A'}
          </CourseType>
          <CourseTitle className="text-lg leading-tight" style={{ color: themeColors.textPrimary }}>
            {course.title}
          </CourseTitle>
        </div>
      </div>
      
      <div className="mb-3">
        <div className="h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: themeColors.borderSubtle }}>
          <div 
            className="h-full transition-all duration-300 rounded-full" 
            style={{ 
              width: `${progressData.progress || 0}%`,
              backgroundColor: themeColors.accent
            }} 
          />
        </div>
      </div>

      <LabelSmall style={{ color: themeColors.textSecondary }}>
        {progressData.completed} of {progressData.total} lessons completed
      </LabelSmall>
    </button>
  );
};

/**
 * Quick Action Card
 * Simple card for navigation to main sections
 */
const QuickActionCard = ({ title, description, onClick, icon }) => {
  const { themeColors } = useTheme();
  return (
    <button
      type="button"
      className="w-full text-left rounded-lg p-6 cursor-pointer transform hover:scale-[1.02] transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-offset-2"
      style={{ 
        backgroundColor: themeColors.card, 
        borderColor: themeColors.border 
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = themeColors.accent;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = themeColors.border;
      }}
      onClick={onClick}
    >
      <div className="flex items-center justify-between mb-2">
        <TitleMedium style={{ color: themeColors.textPrimary }}>{title}</TitleMedium>
        {/* Icon is decorative; title conveys the action */}
        {icon && <span className="text-2xl" aria-hidden="true">{icon}</span>}
      </div>
      {description && (
        <BodyMedium className="text-sm" style={{ color: themeColors.textSecondary }}>
          {description}
        </BodyMedium>
      )}
    </button>
  );
};

const Home = ({ 
  onNavigateToCourses, 
  onNavigateToProfile, 
  onNavigateToPrompts,
  onNavigateToWritersRoom, 
  onLogOut, 
  onModeSwitch,
  showAdminMode = false
}) => {
  const navigate = useNavigate();
  const { 
    getMostRecentCourse, 
    findNextIncompleteLesson, 
    isContentActive, 
    isContentCompleted
  } = useProgress();
  const { user } = useAuth();
  const { themeColors } = useTheme();
  
  // State for continue learning section
  const [continueLearningData, setContinueLearningData] = useState(null);
  const [homeError, setHomeError] = useState(null);
  const [loadingContinueLearning, setLoadingContinueLearning] = useState(true);
  
  // State for active courses
  const [courses, setCourses] = useState([]);
  const [courseProgress, setCourseProgress] = useState({});
  const [loadingCourses, setLoadingCourses] = useState(true);
  
  // State for recent activity
  const [recentActivity, setRecentActivity] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(true);

  /**
   * Fetch continue learning data (most recent course/lesson)
   */
  useEffect(() => {
    const fetchContinueLearning = async () => {
      if (!user) {
        setLoadingContinueLearning(false);
        return;
      }

      try {
        setLoadingContinueLearning(true);
        // Get most recent course
        const recentCourse = await getMostRecentCourse();
        
        if (!recentCourse || !recentCourse.courseId) {
          setLoadingContinueLearning(false);
          return;
        }

        // Get course details
        const courseRef = doc(db, 'courses', recentCourse.courseId);
        const courseSnap = await getDoc(courseRef);
        
        if (!courseSnap.exists()) {
          setLoadingContinueLearning(false);
          return;
        }

        const course = { id: courseSnap.id, ...courseSnap.data() };

        // Get all lessons for the course
        const lessonsRef = collection(db, 'lessons');
        const lessonsQuery = query(lessonsRef, where('course_id', '==', recentCourse.courseId));
        const lessonsSnapshot = await getDocs(lessonsQuery);
        const allLessons = lessonsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Find the current active lesson
        const currentLesson = allLessons.find(lesson => isContentActive(lesson.id)) || 
                             findNextIncompleteLesson(recentCourse.courseId, allLessons);

        if (currentLesson) {
          // Calculate progress for the level
          const levelLessons = allLessons.filter(l => l.level === currentLesson.level);
          const completedLessons = levelLessons.filter(l => isContentCompleted(l.id)).length;
          const totalLessons = levelLessons.length;
          const progressPercentage = totalLessons > 0 ? (completedLessons / totalLessons) * 100 : 0;
          
          setContinueLearningData({
            course,
            lesson: currentLesson,
            level: currentLesson.level,
            allLessons,
            progressPercentage
          });
        }
      } catch (error) {
        console.error('Error fetching continue learning data:', error);
        setHomeError('Unable to load your learning progress. Please refresh the page.');
      } finally {
        setLoadingContinueLearning(false);
      }
    };

    fetchContinueLearning();
  }, [user, getMostRecentCourse, findNextIncompleteLesson, isContentActive, isContentCompleted]);

  /**
   * Fetch all courses and their progress
   */
  useEffect(() => {
    const fetchCourses = async () => {
      if (!user) {
        setLoadingCourses(false);
        return;
      }

      try {
        setLoadingCourses(true);
        const coursesRef = collection(db, 'courses');
        const snapshot = await getDocs(coursesRef);
        const coursesList = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCourses(coursesList);

        // Calculate progress for each course
        const progressData = {};
        for (const course of coursesList) {
          const lessonsRef = collection(db, 'lessons');
          const courseQuery = query(lessonsRef, where('course_id', '==', course.id));
          const courseSnapshot = await getDocs(courseQuery);
          const lessons = courseSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          
          if (lessons.length === 0) {
            progressData[course.id] = { total: 0, completed: 0, hasActive: false, progress: 0 };
            continue;
          }

          const completedLessons = lessons.filter(lesson => isContentCompleted(lesson.id));
          const hasActiveLessons = lessons.some(lesson => isContentActive(lesson.id));
          const total = lessons.length;
          const completed = completedLessons.length;
          const progress = total > 0 ? (completed / total) * 100 : 0;

          progressData[course.id] = { total, completed, hasActive: hasActiveLessons, progress };
        }
        setCourseProgress(progressData);
      } catch (error) {
        console.error('Error fetching courses:', error);
      } finally {
        setLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [user, isContentCompleted, isContentActive]);

  /**
   * Fetch recent activity (completed lessons and prompts)
   */
  useEffect(() => {
    const fetchRecentActivity = async () => {
      if (!user) {
        setLoadingActivity(false);
        return;
      }

      try {
        setLoadingActivity(true);
        const activity = [];

        // Get recently completed lessons
        try {
          const userLessonsRef = collection(db, 'user_lessons');
          // Try with orderBy first, fallback to without if index doesn't exist
          let lessonsSnapshot;
          try {
            const userLessonsQuery = query(
              userLessonsRef,
              where('userId', '==', user.uid),
              where('completed', '==', true),
              orderBy('lastUpdated', 'desc'),
              limit(10)
            );
            lessonsSnapshot = await getDocs(userLessonsQuery);
          } catch (orderByError) {
            // Fallback: get all completed lessons and sort in memory
            const userLessonsQuery = query(
              userLessonsRef,
              where('userId', '==', user.uid),
              where('completed', '==', true)
            );
            lessonsSnapshot = await getDocs(userLessonsQuery);
          }
          
          const lessonsData = [];
          for (const docSnap of lessonsSnapshot.docs) {
            const data = docSnap.data();
            const lessonRef = doc(db, 'lessons', data.lessonId);
            const lessonSnap = await getDoc(lessonRef);
            
            if (lessonSnap.exists()) {
              const lesson = { id: lessonSnap.id, ...lessonSnap.data() };
              const courseRef = doc(db, 'courses', lesson.course_id);
              const courseSnap = await getDoc(courseRef);
              
              if (courseSnap.exists()) {
                lessonsData.push({
                  type: 'lesson',
                  title: lesson.title || `Lesson ${lesson.lesson_number}`,
                  course: { id: courseSnap.id, ...courseSnap.data() },
                  completedAt: data.lastUpdated || data.last_updated || new Date().toISOString()
                });
              }
            }
          }
          
          // Sort by completion date if not already sorted
          lessonsData.sort((a, b) => {
            const aTime = new Date(a.completedAt).getTime();
            const bTime = new Date(b.completedAt).getTime();
            return bTime - aTime;
          });
          
          activity.push(...lessonsData.slice(0, 5));
        } catch (error) {
          console.error('Error fetching completed lessons:', error);
        }

        // Get recently completed prompts
        try {
          const userPromptsRef = collection(db, 'user_prompts');
          let promptsSnapshot;
          try {
            const userPromptsQuery = query(
              userPromptsRef,
              where('userId', '==', user.uid),
              where('completed', '==', true),
              orderBy('lastUpdated', 'desc'),
              limit(5)
            );
            promptsSnapshot = await getDocs(userPromptsQuery);
          } catch (orderByError) {
            // Fallback: get all completed prompts and sort in memory
            const userPromptsQuery = query(
              userPromptsRef,
              where('userId', '==', user.uid),
              where('completed', '==', true)
            );
            promptsSnapshot = await getDocs(userPromptsQuery);
          }
          
          const promptsData = [];
          for (const docSnap of promptsSnapshot.docs) {
            const data = docSnap.data();
            const promptRef = doc(db, 'prompts', data.promptId);
            const promptSnap = await getDoc(promptRef);
            
            if (promptSnap.exists()) {
              promptsData.push({
                type: 'prompt',
                title: promptSnap.data().title || 'Writing Prompt',
                completedAt: data.lastUpdated || data.last_updated || new Date().toISOString()
              });
            }
          }
          
          // Sort by completion date if not already sorted
          promptsData.sort((a, b) => {
            const aTime = new Date(a.completedAt).getTime();
            const bTime = new Date(b.completedAt).getTime();
            return bTime - aTime;
          });
          
          activity.push(...promptsData.slice(0, 3));
        } catch (error) {
          console.error('Error fetching completed prompts:', error);
        }

        // Sort all activity by completion date
        activity.sort((a, b) => {
          const aTime = new Date(a.completedAt).getTime();
          const bTime = new Date(b.completedAt).getTime();
          return bTime - aTime;
        });

        setRecentActivity(activity.slice(0, 5)); // Show top 5
      } catch (error) {
        console.error('Error fetching recent activity:', error);
        setRecentActivity([]);
      } finally {
        setLoadingActivity(false);
      }
    };

    fetchRecentActivity();
  }, [user]);
  
  /**
   * Handle continue learning click
   * If user has active course/lesson, navigate to it
   * Otherwise, navigate to courses page for new users
   */
  const handleContinueLearningClick = () => {
    if (continueLearningData && continueLearningData.course && continueLearningData.lesson) {
      const lessonUrl = getLessonUrl(
        continueLearningData.course,
        continueLearningData.lesson.level,
        continueLearningData.lesson.lesson_number
      );
      navigate(lessonUrl);
    } else {
      // New user - navigate to courses page
      handleCoursesClick();
    }
  };

  /**
   * Handle course click
   */
  const handleCourseClick = (course) => {
    const courseSlug = courseNameToSlug(course.course_name);
    navigate(`/courses/${courseSlug}`);
  };

  /**
   * Handle quick action clicks
   */
  const handleCoursesClick = () => {
    if (onNavigateToCourses) {
      onNavigateToCourses();
    }
  };

  const handlePromptsClick = () => {
    if (onNavigateToPrompts) {
      onNavigateToPrompts();
    }
  };

  const handleWritersRoomClick = () => {
    if (onNavigateToWritersRoom) {
      onNavigateToWritersRoom();
    }
  };

  // Filter active courses (courses with progress)
  const activeCourses = courses.filter(course => {
    const progress = courseProgress[course.id];
    return progress && (progress.hasActive || progress.completed > 0);
  });

  return (
    <div>
      <Header
        title="Home"
        onNavigateToHome={() => {}} // Already on home
        onNavigateToCourses={onNavigateToCourses}
        onNavigateToProfile={onNavigateToProfile}
        onNavigateToPrompts={onNavigateToPrompts}
        onNavigateToWritersRoom={onNavigateToWritersRoom}
        onLogOut={onLogOut}
        onModeSwitch={onModeSwitch}
        showAdminMode={showAdminMode}
      />

      {homeError && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" role="alert">
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-4 flex items-center justify-between gap-4">
            <p>{homeError}</p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="min-h-[44px] px-4 py-2 bg-red-100 hover:bg-red-200 rounded-md focus:ring-2 focus:ring-red-500"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Feed-style Dashboard with Right Sidebar */}
      <div className="flex flex-col lg:flex-row gap-8 pt-6 pb-12 px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-100px)] max-w-7xl mx-auto">
        {/* Main Content Area */}
        <div className="flex-1 min-w-0">
          {/* Hero Section: Continue Learning */}
          <div className="mb-8">
            {/* role="heading" aria-level="1" gives this div page-heading semantics without changing the element */}
            <TitleLarge role="heading" aria-level={1} className="mb-6" style={{ color: themeColors.textPrimary }}>Continue Learning</TitleLarge>
            <ContinueLearningCard
              course={continueLearningData?.course}
              lesson={continueLearningData?.lesson}
              level={continueLearningData?.level}
              allLessons={continueLearningData?.allLessons}
              progressPercentage={continueLearningData?.progressPercentage}
              onClick={handleContinueLearningClick}
              loading={loadingContinueLearning}
            />
          </div>

          {/* Active Courses Section */}
          {activeCourses.length > 0 && (
            <div className="mb-8">
              <TitleMedium role="heading" aria-level={2} className="mb-4" style={{ color: themeColors.textPrimary }}>Active Courses</TitleMedium>
              <div className="space-y-4">
                {activeCourses.map((course) => (
                  <ActiveCourseCard
                    key={course.id}
                    course={course}
                    progressData={courseProgress[course.id]}
                    onClick={() => handleCourseClick(course)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Recent Activity Section */}
          {recentActivity.length > 0 && (
            <div className="mb-8">
              <TitleMedium role="heading" aria-level={2} className="mb-4" style={{ color: themeColors.textPrimary }}>Recent Activity</TitleMedium>
              <div className="rounded-lg p-6 border" style={{ backgroundColor: themeColors.card, borderColor: themeColors.border }}>
                <div className="space-y-3">
                  {recentActivity.map((item, index) => (
                    <div key={index} className="flex items-center justify-between py-2 border-b last:border-0" style={{ borderColor: themeColors.divider }}>
                      <div className="flex items-center gap-3">
                        {/* sr-only communicates the "completed" meaning conveyed only by the green color */}
                        <div className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true"></div>
                        <span className="sr-only">Completed: </span>
                        <div>
                          <BodyMedium style={{ color: themeColors.textPrimary }}>
                            {item.type === 'lesson' ? 'Completed lesson' : 'Completed prompt'}
                          </BodyMedium>
                          <LabelSmall style={{ color: themeColors.textSecondary }}>{item.title}</LabelSmall>
                        </div>
                      </div>
                      {item.completedAt && (
                        <LabelSmall style={{ color: themeColors.textTertiary }}>
                          {new Date(item.completedAt).toLocaleDateString()}
                        </LabelSmall>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar: Quick Actions */}
        <aside 
          className="w-full lg:w-80 flex-shrink-0 lg:pl-8 lg:border-l"
          style={{ borderColor: themeColors.border }}
        >
          <div className="lg:sticky lg:top-24">
            <TitleMedium role="heading" aria-level={2} className="mb-4" style={{ color: themeColors.textPrimary }}>Quick Actions</TitleMedium>
            <div className="space-y-3">
              <QuickActionCard
                title="Browse Courses"
                description="Explore all available courses"
                onClick={handleCoursesClick}
                icon="📚"
              />
              <QuickActionCard
                title="Writing Prompts"
                description="Practice with creative prompts"
                onClick={handlePromptsClick}
                icon="✍️"
              />
              <QuickActionCard
                title="The Writers Room"
                description="Your creative workspace"
                onClick={handleWritersRoomClick}
                icon="🖋️"
              />
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default Home;
